//npm packages
import React, { memo, useState } from 'react';
import { Button, Input } from 'reactstrap';
import swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faSearch,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

//redux actions
import { licenseList } from '../../redux/actions';

//elements starts with capital letter
import { GridBase } from '../../components';

//helper files
import strings from '../../globalization';
import consts from '../../consts';

import utility from '../../utility';

const onDownloadLicense = (row, showMessage) => {
  const defaultCATSoftwareVersion =
    row.defaultCATSoftwareVersion?.CATSoftwareVersion;
  const selectedCATSoftwareVersion =
    row.selectedCATSoftwareVersion?.CATSoftwareVersion;
  const hasCATSoftwareVersionSelected = Boolean(selectedCATSoftwareVersion);

  const isDifferentFromDefault =
    selectedCATSoftwareVersion !== defaultCATSoftwareVersion;
  if (hasCATSoftwareVersionSelected && isDifferentFromDefault) {
    return swal
      .fire({
        title: `Are you sure the Customer is on CAT ${selectedCATSoftwareVersion}? The default CAT Software Version is CAT ${defaultCATSoftwareVersion}.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      })
      .then(result => {
        if (result.isConfirmed) {
          utility.onLicenseDownload(row._id, row, showMessage);
        }
      });
  }

  utility.onLicenseDownload(row._id, row, showMessage);
};

const onCertficateDownload = row => {
  utility.onDownloadCertificate(row, true);
};
const License = memo(props => {
  const history = useHistory();
  const { pathname } = useLocation();
  const showMessage = pathname.includes('managelicense');
  const { params } = props.match;
  const [addressId, setAddressId] = useState('');
  const [applySearch, setApplySearch] = useState(false);
  const data = useSelector(state => {
    return { settingData: state.settingData, loginData: state.loginData };
  }, shallowEqual);
  const IsSpecialist =
    consts.UserRoleId.Specialist ===
    data?.loginData?.data?.userDetail?.UserRole;

  const loginData = useSelector(state => {
    const { userDetail } = state.loginData.data || {};
    return userDetail;
  }, shallowEqual);
  const showHideAddButton =
    loginData && !consts.UserRoleId.Viewer.includes(loginData.UserRole);
  const shouldNotDownload =
    loginData && consts.UserRoleId.SuperAdmin.includes(loginData.UserRole);

  const generateLicense = ({ row }) => {
    return (
      <div>
        <Button
          size="sm"
          color="primary"
          className="commandBtn"
          disabled={row.disableDownload || shouldNotDownload}
          onClick={() => onDownloadLicense(row, pathname, showMessage)}
          title={
            row.disableDownload
              ? strings.LicenseDownloadButton
              : strings.Download
          }
        >
          <FontAwesomeIcon icon={faDownload} /> {strings.Download}{' '}
        </Button>
      </div>
    );
  };

  const downloadCertificate = ({ row }) => {
    const hasCATSync = row.ProductType.includes(consts.ProductType.catsync);
    const disabled =
      row.disableCertificateDownload ||
      shouldNotDownload ||
      row.IsActive !== consts.IsActive ||
      !hasCATSync;
    return (
      <div>
        <Button
          size="sm"
          color="primary"
          className="commandBtn"
          disabled={disabled}
          onClick={() => onCertficateDownload(row)}
          title={strings.DownloadCertificate}
        >
          <FontAwesomeIcon icon={faDownload} />
          {strings.DownloadAction}{' '}
        </Button>
      </div>
    );
  };
  const ViewPassword = ({ row }) => {
    const [passwordDisplay, setPasswordDisplay] = useState(false);
    return (
      <div style={{ position: 'relative' }}>
        <Input
          name="Password"
          type={passwordDisplay ? 'text' : 'password'}
          style={{ border: 'none', backgroundColor: 'transparent' }}
          defaultValue={`${consts.defaultPassword}${row.ClientId}`}
          readOnly={true}
        />
        {IsSpecialist ? (
          <span>
            <FontAwesomeIcon
              style={{
                position: 'absolute',
                right: '0px',
                top: '30%',
                cursor: 'pointer'
              }}
              onClick={() => setPasswordDisplay(!passwordDisplay)}
              icon={passwordDisplay ? faEyeSlash : faEye}
            />
          </span>
        ) : (
          ''
        )}
      </div>
    );
  };
  const handleAdd = props => {
    history.push({ pathname: '/site/new' });
  };

  const columns = [
    { key: 'ClientId', name: strings.ClientId, width: 100, type: 'number' },
    {
      key: 'SiteName',
      name: strings.SiteName,
      width: 150
    },
    {
      key: 'ProductTypeName',
      name: strings.ProductType,
      width: 150,
      mode: 'remote',
      store: 'ProductType',
      valueProp: 'DisplayValue',
      displayValue: 'DisplayValue'
    },
    {
      key: 'LicenseType',
      name: strings.LicenseType,
      width: 150
    },
    {
      key: 'LicenseAction',
      type: 'action',
      hidden: shouldNotDownload,
      name: strings.Actions,
      getRowMetaData: row => row,
      formatter: generateLicense,
      width: 150
    },
    {
      key: 'CertificateAction',
      type: 'action',
      hidden: shouldNotDownload,
      name: strings.Actions,
      getRowMetaData: row => row,
      formatter: downloadCertificate,
      width: 200
    },
    {
      key: 'AddressId',
      name: strings.AddressId,
      width: 150
    },
    {
      key: 'AccountNumber',
      name: strings.AccountNumber,
      width: 150
    },
    {
      key: 'Region',
      name: strings.Region,
      width: 150,
      type: 'select',
      mode: 'remote',
      store: 'Region',
      valueProp: 'DisplayValue',
      displayValue: 'DisplayValue'
    },
    {
      key: 'CountryCode',
      name: strings.CountryCode,
      type: 'select',
      store: 'CountryCode',
      mode: 'remote',
      valueProp: 'DisplayValue',
      displayValue: 'DisplayValue'
    },
    {
      key: 'InitialSOA',
      name: strings.InitialSOA,
      hidden: true,
      width: 150
    },
    {
      key: 'MostRecentSOA',
      name: strings.MostRecentSOA,
      hidden: true,
      width: 150
    },
    {
      key: 'Dealer',
      name: strings.Dealer,
      hidden: true,
      width: 150
    },

    {
      key: 'IDN',
      name: strings.IDN,
      hidden: true,
      width: 150
    },
    {
      key: 'IsActive',
      name: strings.LicStatus,
      hidden: true,
      width: 100,
      mode: 'remote',
      store: 'Status',
      valueProp: '_id',
      displayValue: 'DisplayValue'
    },
    {
      key: 'ExpirationDate',
      name: strings.ExpirationDate,
      hidden: true,
      type: 'date',
      width: 150,
      format: 'DD/MM/YYYY'
    },
    {
      key: 'createdAt',
      name: strings.CreatedOn,
      hidden: true,
      type: 'date',
      width: 150,
      format: 'DD/MM/YYYY'
    },
    {
      key: 'CreatedByName',
      name: strings.CreatedBy,
      hidden: true,
      width: 150,
      mode: 'remote',
      store: 'User',
      valueProp: 'DisplayValue',
      displayValue: 'UserName'
    },
    {
      key: 'updatedAt',
      name: strings.updatedAt,
      hidden: true,
      type: 'date',
      width: 150,
      format: 'DD/MM/YYYY'
    },
    {
      key: 'ModifiedByName',
      name: strings.UpdatedBy,
      hidden: true,
      width: 150,
      mode: 'remote',
      store: 'User',
      valueProp: 'DisplayValue',
      displayValue: 'UserName'
    },
    {
      name: strings.CountryCode,
      key: 'DefaultLanguage',
      name: strings.DefaultLanguage,
      hidden: true,
      width: 150,
      mode: 'remote',
      store: 'Language',
      valueProp: '_id',
      displayValue: 'DisplayValue'
    },
    {
      name: strings.State,
      key: 'State',
      hidden: true
    },
    {
      name: strings.City,
      key: 'City',
      hidden: true
    },
    {
      key: 'Password',
      name: strings.PasswordView,
      hidden: false,
      width: 190,
      formatter: ViewPassword
    },
    {
      key: 'TimeZone',
      name: strings.TimeZone,
      hidden: true,
      width: 150,
      type: 'select',
      mode: 'remote',
      store: 'TimeZones',
      valueProp: 'DisplayValue',
      displayValue: 'DisplayValue'
    },
    {
      key: 'TrackingSystem',
      name: strings.TrackingSystem,
      hidden: true,
      type: 'select',
      mode: 'remote',
      store: 'TrackingSystem',
      valueProp: '_id',
      displayValue: 'DisplayValue',
      width: 150
    }
  ];

  const advanceFilters = (
    <>
      <div className="btn-group mr-1" role="group" aria-label="First group">
        <Input
          className="searchFilter"
          onChange={e => setAddressId(e.target.value)}
          value={addressId}
          placeholder="Address Id / Serial Number / SOA"
        ></Input>
        <Button
          className="searchButton"
          onClick={() => setApplySearch(true)}
          color="primary"
        >
          <FontAwesomeIcon className="ls" icon={faSearch} />
        </Button>
        <Button
          className="clearButton"
          onClick={() => {
            setAddressId('');
            setApplySearch(true);
          }}
          color="danger"
        >
          <i className="fa fa-times"></i>
        </Button>
      </div>
    </>
  );
  return (
    <GridBase
      {...props}
      columns={columns}
      action={licenseList}
      title={strings.ManageLicense}
      showAddButton={showHideAddButton}
      enableDoubleClick={showHideAddButton}
      disableAdd={shouldNotDownload}
      dataProperty="licenseListData"
      detailRoute="licenseDetail"
      handleAdd={handleAdd}
      comboType={[
        'Language',
        'ProductType',
        'Status',
        'Facility',
        'User',
        'Preference',
        'Region',
        'TimeZones',
        'CountryCode',
        'TrackingSystem'
      ]}
      loadPref={true}
      listId="License"
      advanceFilters={advanceFilters}
      baseFilter={{ search: addressId }}
      applySearch={applySearch}
      setApplySearch={setApplySearch}
      setAddressId={setAddressId}
    />
  );
});

export default License;
